/**
 * useDragAndDrop Hook
 *
 * Encapsulates drag-and-drop logic using @dnd-kit
 *
 * Features:
 * - Pointer and keyboard sensor support
 * - Accessible drag-drop (WCAG compliant)
 * - Smooth animations
 * - Job reassignment on drop
 */

import { useState } from 'react';
import {
  DragStartEvent,
  DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { JobAssignment } from '../types/dispatch.types';

interface UseDragAndDropProps {
  onReassignJob: (assignmentId: string, newDriverId: string, estimatedStartTime: string) => void;
  assignments: JobAssignment[];
}

export function useDragAndDrop({ onReassignJob, assignments }: UseDragAndDropProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure sensors for both mouse/touch and keyboard navigation
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over) {
      return; // Dropped outside a valid drop zone
    }

    const assignmentId = active.id as string;
    const newDriverId = over.id as string;

    // Find the assignment being dragged
    const assignment = assignments.find((a) => a.id === assignmentId);

    if (!assignment) {
      return;
    }

    // Don't reassign if dropped on same driver
    if (assignment.driverId === newDriverId) {
      return;
    }

    // Reassign the job
    onReassignJob(assignmentId, newDriverId, assignment.estimatedStartTime);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Get the currently dragged assignment for overlay rendering
  const activeAssignment = activeId ? assignments.find((a) => a.id === activeId) : null;

  return {
    sensors,
    activeId,
    activeAssignment,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}
